import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { detectDevice, generateDeviceId } from "../../utils/detectDevice";
import {
  getRealityStream,
  heartbeatRealityStream,
  joinRealityStream,
  postRealityStreamInteraction,
  realityStreamEventsUrl,
} from "../../api/realityStreamApi";

const HEARTBEAT_MS = 30000;
const FALLBACK_POLL_MS = 2000;

function mergeEvent(session, event) {
  if (!session || !event) return session;
  const payload = event.payload || {};
  const next = { ...session, sequence: Math.max(Number(session.sequence || 0), Number(event.sequence || 0)) };
  if (payload.presence) next.presence = payload.presence;
  if (payload.state) {
    next.authoritativeState = payload.state;
    if (payload.stateVersion != null) next.stateVersion = Number(payload.stateVersion);
    if (payload.state?.playback?.status) next.playbackState = payload.state.playback.status;
  }
  if (event.kind === "session.ended") next.playbackState = "ended";
  return next;
}

export default function useRealityStreamSession({
  sessionCode = null,
  enabled = true,
  initialRole = "viewer",
} = {}) {
  const [session, setSession] = useState(null);
  const [connection, setConnection] = useState("idle");
  const [error, setError] = useState("");
  const [role, setRole] = useState(initialRole === "participant" ? "participant" : "viewer");
  const [lastEvent, setLastEvent] = useState(null);
  const deviceIdRef = useRef(generateDeviceId());
  const deviceTypeRef = useRef(detectDevice());
  const sourceRef = useRef(null);
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);
  const mountedRef = useRef(true);
  const lastSequenceRef = useRef(0);

  const code = useMemo(() => String(sessionCode || "").trim().toUpperCase(), [sessionCode]);

  const applySession = useCallback((next) => {
    if (!next || !mountedRef.current) return;
    lastSequenceRef.current = Math.max(lastSequenceRef.current, Number(next.sequence || 0));
    setSession(next);
  }, []);

  const applyEvent = useCallback((event) => {
    if (!event || !mountedRef.current) return;
    lastSequenceRef.current = Math.max(lastSequenceRef.current, Number(event.sequence || 0));
    setLastEvent(event);
    setSession(current => mergeEvent(current, event));
  }, []);

  const stopTransport = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (!code || pollRef.current) return;
    setConnection("polling");
    pollRef.current = window.setInterval(() => {
      getRealityStream(code)
        .then(result => {
          if (result?.session) applySession(result.session);
        })
        .catch(() => {});
    }, FALLBACK_POLL_MS);
  }, [applySession, code]);

  const connect = useCallback(() => {
    if (!code || !enabled || typeof EventSource === "undefined") {
      if (code && enabled) startPolling();
      return;
    }
    stopTransport();
    setConnection("connecting");
    const source = new EventSource(realityStreamEventsUrl(code, lastSequenceRef.current), { withCredentials: true });
    sourceRef.current = source;
    source.onopen = () => {
      if (!mountedRef.current) return;
      setConnection("live");
    };
    source.onmessage = message => {
      if (!mountedRef.current) return;
      try {
        const envelope = JSON.parse(message.data);
        if (envelope?.type === "snapshot" && envelope.session) applySession(envelope.session);
        else if (envelope?.type === "event" && envelope.event) applyEvent(envelope.event);
        else if (envelope?.type === "ended" && envelope.session) applySession(envelope.session);
      } catch {}
    };
    source.onerror = () => {
      if (!mountedRef.current) return;
      source.close();
      sourceRef.current = null;
      startPolling();
    };
  }, [applyEvent, applySession, code, enabled, startPolling, stopTransport]);

  const join = useCallback(async (nextRole = initialRole) => {
    if (!code || !enabled) return null;
    const safeRole = nextRole === "participant" ? "participant" : "viewer";
    const result = await joinRealityStream(code, {
      deviceId: deviceIdRef.current,
      label: `${deviceTypeRef.current} ${safeRole}`,
      type: deviceTypeRef.current,
      role: safeRole,
    });
    if (result?.session) applySession(result.session);
    setRole(result?.role === "participant" ? "participant" : "viewer");
    return result;
  }, [applySession, code, enabled, initialRole]);

  useEffect(() => {
    mountedRef.current = true;
    setSession(null);
    setError("");
    lastSequenceRef.current = 0;
    if (!code || !enabled) return undefined;

    join(initialRole)
      .then(result => {
        if (!mountedRef.current || !result?.session) return;
        connect();
      })
      .catch(err => {
        if (!mountedRef.current) return;
        setError(err?.response?.data?.message || "Live Reality could not be joined.");
        setConnection("error");
      });

    heartbeatRef.current = window.setInterval(() => {
      heartbeatRealityStream(code, deviceIdRef.current).catch(() => {});
    }, HEARTBEAT_MS);

    return () => {
      mountedRef.current = false;
      stopTransport();
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    };
  }, [code, connect, enabled, initialRole, join, stopTransport]);

  const joinAsParticipant = useCallback(async () => join("participant"), [join]);
  const joinAsViewer = useCallback(async () => join("viewer"), [join]);

  const interact = useCallback(async (interaction) => {
    if (!code || role !== "participant") return false;
    try {
      await postRealityStreamInteraction(code, {
        deviceId: deviceIdRef.current,
        interaction,
      });
      return true;
    } catch {
      return false;
    }
  }, [code, role]);

  return {
    session,
    state: session?.authoritativeState || null,
    connection,
    error,
    role,
    lastEvent,
    deviceId: deviceIdRef.current,
    joinAsParticipant,
    joinAsViewer,
    interact,
    reconnect: connect,
  };
}

export { mergeEvent };
